import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { AppShell } from "../components/AppShell";
import { Button } from "../components/ui/button";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { StrengthBadge } from "../components/Badges";
import { ProvenanceBadge, ProvenanceLegend, SourceLink } from "../components/Provenance";
import {
  ArrowLeft, RefreshCw, Loader2, Globe, Users, Package, DollarSign, Building2,
  Share2, Star, MessageSquare, Newspaper, Landmark, Swords, GitCompareArrows,
  ThumbsUp, ThumbsDown, Zap, Rocket, ExternalLink, Search, CheckCircle2, AlertCircle,
} from "lucide-react";

const RESEARCH_STAGES = [
  "Reading the official website",
  "Verifying official social profiles",
  "Scanning review platforms",
  "Collecting news & press",
  "Mapping competitors & analogues",
  "Structuring the intelligence profile",
];

function Researching() {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setActive((a) => Math.min(a + 1, RESEARCH_STAGES.length - 1)), 4000);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="max-w-xl mx-auto pt-6">
      <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-sm">
        <div className="flex items-center gap-2 mb-6">
          <Search className="w-5 h-5 text-blue-600" />
          <h2 className="font-display text-xl font-semibold text-slate-900">Researching your digital footprint</h2>
        </div>
        <div className="space-y-4">
          {RESEARCH_STAGES.map((s, i) => {
            const done = i < active, cur = i === active;
            return (
              <motion.div key={s} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.08 }} className="flex items-center gap-3">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${done ? "bg-emerald-500" : cur ? "bg-blue-500" : "bg-slate-100"}`}>
                  {done ? <CheckCircle2 className="w-4 h-4 text-white" /> : cur ? <Loader2 className="w-3.5 h-3.5 text-white animate-spin" /> : <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />}
                </div>
                <span className={`text-sm ${done || cur ? "text-slate-900 font-medium" : "text-slate-400"}`}>{s}</span>
              </motion.div>
            );
          })}
        </div>
        <p className="text-xs text-slate-400 mt-8 font-mono">Researching once and caching the result. This can take a minute.</p>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid={testid}>
      <h3 className="font-display text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
        <Icon className="w-4.5 h-4.5 text-slate-400" /> {title}
      </h3>
      {children}
    </div>
  );
}

function Item({ children, type, url }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-slate-100 last:border-0">
      <div className="text-sm text-slate-700 leading-relaxed min-w-0">{children}</div>
      <div className="flex items-center gap-2 shrink-0">
        {type && <ProvenanceBadge type={type} size="xs" />}
        <SourceLink url={url} />
      </div>
    </div>
  );
}

function empty(arr) { return !arr || arr.length === 0; }

export default function BusinessIntel() {
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "loading" });
  const pollRef = useRef(null);

  const fetchOnce = useCallback(async () => {
    try {
      const { data } = await api.get("/business/intel");
      setState(data);
      return data;
    } catch (e) {
      console.error("Failed to load business intel", e);
      setState({ status: "error", error: "Could not load your intelligence profile." });
      return { status: "error" };
    }
  }, []);

  useEffect(() => {
    let stop = false;
    const tick = async () => {
      const d = await fetchOnce();
      if (stop) return;
      if (d.status === "researching") pollRef.current = setTimeout(tick, 4000);
    };
    tick();
    return () => { stop = true; clearTimeout(pollRef.current); };
  }, [fetchOnce]);

  const runResearch = async () => {
    try {
      await api.post("/business/research");
      setState({ status: "researching" });
      const tick = async () => {
        const d = await fetchOnce();
        if (d.status === "researching") pollRef.current = setTimeout(tick, 4000);
      };
      tick();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const header = (
    <button onClick={() => navigate("/dashboard")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-5" data-testid="intel-back">
      <ArrowLeft className="w-4 h-4" /> Dashboard
    </button>
  );

  if (state.status === "loading") return <AppShell>{header}<div className="text-center py-20 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div></AppShell>;

  if (state.status === "researching") return <AppShell>{header}<Researching /></AppShell>;

  if (state.status === "none" || state.status === "error" || !state.bip) {
    return (
      <AppShell>{header}
        <div className="max-w-xl mx-auto text-center py-16">
          <div className="w-12 h-12 rounded-xl bg-slate-900 flex items-center justify-center mx-auto mb-5">
            <Search className="w-6 h-6 text-white" />
          </div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 mb-2">Build your Business Intelligence profile</h1>
          <p className="text-slate-500 mb-2">We research your full public digital footprint — website, official social accounts, reviews, news, funding, competitors and close analogues — from real sources, then cache it as reusable context for every decision.</p>
          {state.status === "error" && <p className="text-sm text-rose-600 mb-4">{state.error || "Research failed. Try again."}</p>}
          <Button onClick={runResearch} data-testid="run-research-button" className="bg-slate-900 hover:bg-slate-800 rounded-lg h-11 px-6 gap-2 mt-4">
            <Search className="w-4 h-4" /> Run deep research
          </Button>
        </div>
      </AppShell>
    );
  }

  const b = state.bip;
  const comp = b.company || {};
  return (
    <AppShell>
      {header}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Business Intelligence</h1>
            {b.data_confidence && <StrengthBadge value={b.data_confidence} label="data confidence" />}
          </div>
          <p className="text-slate-500 text-sm">Cached research — reused as compact context for every decision. Refresh only when things change.</p>
        </div>
        <Button variant="outline" onClick={runResearch} data-testid="refresh-research-button" className="rounded-lg gap-2"><RefreshCw className="w-4 h-4" /> Refresh</Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm mb-6">
        <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-2">How to read the labels</p>
        <ProvenanceLegend />
      </div>

      {b.notes && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-sm text-amber-900">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {b.notes}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Company */}
        <Section icon={Building2} title="Company" testid="intel-company">
          <p className="text-sm text-slate-700 leading-relaxed">{comp.summary}</p>
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 mt-4">
            {comp.founded && <div><span className="text-[10px] uppercase tracking-wider text-slate-400">Founded</span><p className="text-sm text-slate-800">{comp.founded}</p></div>}
            {comp.headquarters && <div><span className="text-[10px] uppercase tracking-wider text-slate-400">HQ</span><p className="text-sm text-slate-800">{comp.headquarters}</p></div>}
            {!empty(comp.locations) && <div className="col-span-2"><span className="text-[10px] uppercase tracking-wider text-slate-400">Locations</span><p className="text-sm text-slate-800">{comp.locations.join(", ")}</p></div>}
            {comp.contact && (comp.contact.email || comp.contact.phone || comp.contact.address) && (
              <div className="col-span-2"><span className="text-[10px] uppercase tracking-wider text-slate-400">Public contact</span>
                <p className="text-sm text-slate-800">{[comp.contact.email, comp.contact.phone, comp.contact.address].filter(Boolean).join(" · ")}</p></div>
            )}
          </div>
          <div className="mt-3 flex items-center gap-2"><ProvenanceBadge type={comp.source_type} size="xs" /><SourceLink url={comp.source_url} /></div>
        </Section>

        {/* Business model */}
        <Section icon={Landmark} title="Business Model" testid="intel-model">
          <p className="text-sm text-slate-800 font-medium">{b.business_model?.model}</p>
          <p className="text-sm text-slate-600 mt-1 leading-relaxed">{b.business_model?.how_they_make_money}</p>
          <div className="mt-3"><ProvenanceBadge type={b.business_model?.source_type} size="xs" /></div>
          <div className="mt-4 pt-4 border-t border-slate-100">
            <span className="text-[10px] uppercase tracking-wider text-slate-400">Customers & market</span>
            <p className="text-sm text-slate-700 mt-1">{b.customers_market?.target_customers}</p>
            {b.customers_market?.market && <p className="text-xs text-slate-500 mt-1">{b.customers_market.market}</p>}
            {!empty(b.customers_market?.segments) && <p className="text-xs text-slate-500 mt-1">Segments: {b.customers_market.segments.join(", ")}</p>}
          </div>
        </Section>

        {/* Products */}
        <Section icon={Package} title="Products & Services" testid="intel-products">
          {empty(b.products_services) ? <p className="text-sm text-slate-400">Not found.</p> :
            b.products_services.map((p, i) => (
              <Item key={i} type={p.source_type} url={p.source_url}><span className="font-medium text-slate-900">{p.name}</span>{p.description ? ` — ${p.description}` : ""}</Item>
            ))}
        </Section>

        {/* Pricing */}
        <Section icon={DollarSign} title="Pricing" testid="intel-pricing">
          {empty(b.pricing) ? <p className="text-sm text-slate-400">Not publicly listed / not found.</p> :
            b.pricing.map((p, i) => (
              <Item key={i} type={p.source_type} url={p.source_url}><span className="font-medium text-slate-900">{p.plan}</span> <span className="font-mono">{p.price}</span>{p.notes ? ` — ${p.notes}` : ""}</Item>
            ))}
        </Section>

        {/* Social */}
        <Section icon={Share2} title="Social Media & Online Presence" testid="intel-social">
          {empty(b.social_media) ? <p className="text-sm text-slate-400">No verified official profiles found.</p> :
            <div className="space-y-3">
              {b.social_media.map((s, i) => (
                <div key={i} className="border border-slate-200 rounded-lg p-3">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900 text-sm">{s.platform}</span>
                      {s.handle && <span className="text-xs text-slate-500 font-mono">{s.handle}</span>}
                      {s.verified ? <span className="font-mono text-[9px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">VERIFIED</span>
                        : <span className="font-mono text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200">UNVERIFIED</span>}
                    </div>
                    <div className="flex items-center gap-2"><ProvenanceBadge type={s.source_type} size="xs" />{s.url && <SourceLink url={s.url} />}</div>
                  </div>
                  {s.audience && <p className="text-xs text-slate-500 mt-1 font-mono">{s.audience}</p>}
                  {s.description && <p className="text-sm text-slate-600 mt-1">{s.description}</p>}
                  {!empty(s.content_themes) && <p className="text-xs text-slate-500 mt-1">Themes: {s.content_themes.join(", ")}</p>}
                </div>
              ))}
            </div>}
        </Section>

        {/* Reviews */}
        <Section icon={Star} title="Reviews & Customer Feedback" testid="intel-reviews">
          {empty(b.customer_reviews) ? <p className="text-sm text-slate-400">No review data found.</p> :
            <div className="space-y-3">
              {b.customer_reviews.map((r, i) => (
                <div key={i} className="border border-slate-200 rounded-lg p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-slate-900 text-sm">{r.platform} {r.rating && <span className="font-mono text-amber-600">{r.rating}</span>}</span>
                    <div className="flex items-center gap-2"><ProvenanceBadge type={r.source_type || "review"} size="xs" /><SourceLink url={r.url} /></div>
                  </div>
                  {!empty(r.likes) && <p className="text-xs text-emerald-700 mt-1.5"><ThumbsUp className="w-3 h-3 inline mr-1" />{r.likes.join(", ")}</p>}
                  {!empty(r.complaints) && <p className="text-xs text-rose-700 mt-1"><ThumbsDown className="w-3 h-3 inline mr-1" />{r.complaints.join(", ")}</p>}
                  {!empty(r.requested_features) && <p className="text-xs text-slate-500 mt-1">Requested: {r.requested_features.join(", ")}</p>}
                </div>
              ))}
            </div>}
        </Section>
      </div>

      {/* Public sentiment full width */}
      {b.public_sentiment && (b.public_sentiment.summary || !empty(b.public_sentiment.positive_themes)) && (
        <div className="mt-6"><Section icon={MessageSquare} title="Public Sentiment" testid="intel-sentiment">
          <p className="text-sm text-slate-700 leading-relaxed">{b.public_sentiment.summary}</p>
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            {!empty(b.public_sentiment.positive_themes) && <div><span className="text-[10px] uppercase tracking-wider text-emerald-600">Positive themes</span><ul className="mt-1 space-y-1">{b.public_sentiment.positive_themes.map((t, i) => <li key={i} className="text-sm text-slate-700 flex gap-1.5"><ThumbsUp className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" />{t}</li>)}</ul></div>}
            {!empty(b.public_sentiment.negative_themes) && <div><span className="text-[10px] uppercase tracking-wider text-rose-600">Negative themes</span><ul className="mt-1 space-y-1">{b.public_sentiment.negative_themes.map((t, i) => <li key={i} className="text-sm text-slate-700 flex gap-1.5"><ThumbsDown className="w-3.5 h-3.5 text-rose-500 mt-0.5 shrink-0" />{t}</li>)}</ul></div>}
          </div>
          <div className="mt-3"><ProvenanceBadge type={b.public_sentiment.source_type} size="xs" /></div>
        </Section></div>
      )}

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        {/* News */}
        {!empty(b.news_press) && (
          <Section icon={Newspaper} title="News & Press" testid="intel-news">
            {b.news_press.map((n, i) => (
              <Item key={i} type={n.source_type || "third_party"} url={n.url}>
                <span className="font-medium text-slate-900">{n.title}</span>{n.date ? <span className="text-xs text-slate-400 font-mono ml-1">{n.date}</span> : ""}{n.summary ? <span className="block text-slate-500 text-xs mt-0.5">{n.summary}</span> : ""}
              </Item>
            ))}
          </Section>
        )}

        {/* Funding */}
        {!empty(b.funding) && (
          <Section icon={Landmark} title="Funding" testid="intel-funding">
            {b.funding.map((f, i) => (
              <Item key={i} type={f.source_type || "third_party"} url={f.url}>
                <span className="font-medium text-slate-900">{f.round} {f.amount && <span className="font-mono">{f.amount}</span>}</span>{f.date ? <span className="text-xs text-slate-400 ml-1">{f.date}</span> : ""}{!empty(f.investors) ? <span className="block text-slate-500 text-xs">{f.investors.join(", ")}</span> : ""}
              </Item>
            ))}
          </Section>
        )}

        {/* Competitors */}
        <Section icon={Swords} title="Competitors" testid="intel-competitors">
          {empty(b.competitors) ? <p className="text-sm text-slate-400">Not found.</p> :
            b.competitors.map((c, i) => (
              <Item key={i} type={c.source_type} url={c.url}><span className="font-medium text-slate-900">{c.name}</span>{c.why_relevant ? ` — ${c.why_relevant}` : ""}</Item>
            ))}
        </Section>

        {/* Analogues */}
        <Section icon={GitCompareArrows} title="Comparable Businesses (Analogues)" testid="intel-analogues">
          {empty(b.analogues) ? <p className="text-sm text-slate-400">Not found.</p> :
            b.analogues.map((c, i) => (
              <Item key={i} type={c.source_type} url={c.url}><span className="font-medium text-slate-900">{c.name}</span>{c.business_model ? ` (${c.business_model})` : ""}{c.why_comparable ? ` — ${c.why_comparable}` : ""}</Item>
            ))}
        </Section>
      </div>

      {/* SWOT-ish */}
      <div className="grid sm:grid-cols-3 gap-6 mt-6">
        <Section icon={Zap} title="Strengths" testid="intel-strengths">
          {empty(b.strengths) ? <p className="text-sm text-slate-400">—</p> : <ul className="space-y-2">{b.strengths.map((s, i) => <li key={i} className="text-sm text-slate-700 flex items-start justify-between gap-2"><span className="flex gap-1.5"><ThumbsUp className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" />{s.point}</span><ProvenanceBadge type={s.source_type} size="xs" /></li>)}</ul>}
        </Section>
        <Section icon={ThumbsDown} title="Weaknesses" testid="intel-weaknesses">
          {empty(b.weaknesses) ? <p className="text-sm text-slate-400">—</p> : <ul className="space-y-2">{b.weaknesses.map((s, i) => <li key={i} className="text-sm text-slate-700 flex items-start justify-between gap-2"><span>{s.point}</span><ProvenanceBadge type={s.source_type} size="xs" /></li>)}</ul>}
        </Section>
        <Section icon={Rocket} title="Differentiators" testid="intel-differentiators">
          {empty(b.differentiators) ? <p className="text-sm text-slate-400">—</p> : <ul className="space-y-2">{b.differentiators.map((s, i) => <li key={i} className="text-sm text-slate-700 flex items-start justify-between gap-2"><span>{s.point}</span><ProvenanceBadge type={s.source_type} size="xs" /></li>)}</ul>}
        </Section>
      </div>

      {!empty(b.recent_developments) && (
        <div className="mt-6"><Section icon={Zap} title="Important Recent Developments" testid="intel-recent">
          {b.recent_developments.map((r, i) => (
            <Item key={i} type={r.source_type || "third_party"} url={r.url}>{r.point}{r.date ? <span className="text-xs text-slate-400 font-mono ml-1">{r.date}</span> : ""}</Item>
          ))}
        </Section></div>
      )}

      {!empty(b.web_sources) && (
        <div className="mt-6"><Section icon={ExternalLink} title={`Sources consulted (${b.web_sources.length})`} testid="intel-sources">
          <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
            {b.web_sources.map((s, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <span className="font-mono text-[10px] text-slate-400 mt-1 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <a href={s.url} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline break-all flex items-center gap-1">{s.title} <ExternalLink className="w-3 h-3 shrink-0" /></a>
              </li>
            ))}
          </ul>
        </Section></div>
      )}

      <p className="text-xs text-slate-400 text-center font-mono max-w-xl mx-auto my-8">
        Compiled from real public sources. Labels show where each fact came from. AI-inferred items are never presented as verified facts.
      </p>
    </AppShell>
  );
}
